import { createFileRoute } from "@tanstack/react-router";
import { BookOpen, Plus } from "lucide-react";
import { useEffect, useState, useTransition, useRef } from "react";
import { toast } from "sonner";
import { z } from "zod";

import {
  createResourceColumns,
  type ResourceRow,
} from "~/components/engagement/resources/resource-columns";
import type { ResourceDrawerRef } from "~/components/engagement/resources/resource-drawer";
import { ResourceDrawer } from "~/components/engagement/resources/resource-drawer";
import { ResourcePreviewDrawer } from "~/components/engagement/resources/resource-preview-drawer";
import { ResourceTableSkeleton } from "~/components/engagement/resources/resource-skeleton";
import { ErrorStateCard } from "~/components/error-state-card";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { DataTable } from "~/components/ui/data-table";
import { EmptyState } from "~/components/ui/empty-state";
import { PageSection } from "~/components/ui/page-section";
import { TagCombobox } from "~/components/ui/tag-combobox";
import { useResourceDownload } from "~/lib/hooks/useFileDownload";
import {
  useResourcesByCoach,
  useResourceTagsByCoach,
  useDeleteResource,
  useAddResource,
  useUpdateResource,
  useResourceDetail,
} from "~/lib/queries/resourceQuery";
import type {
  AddEngagementResourceInput,
  UpdateEngagementResourceInput,
} from "~/lib/schemas/resource";
import type { PreviewResource } from "~/lib/types/resource";

export const Route = createFileRoute(
  "/_user/my-coaching/clients/programs/$engagementId/coaching-resources",
)({
  staticData: { title: "Coaching resources" },
  validateSearch: z.object({ create: z.boolean().optional() }),
  component: CoachingResourcesPage,
});

function CoachingResourcesPage() {
  const { engagementId } = Route.useParams();
  const { create } = Route.useSearch();

  const addResource = useAddResource();
  const deleteResource = useDeleteResource();
  const updateResource = useUpdateResource();

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [isPending, startTransition] = useTransition();

  const [previewResource, setPreviewResource] =
    useState<PreviewResource | null>(null);
  const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(
    null,
  );
  const resourceDrawerRef = useRef<ResourceDrawerRef>(null);
  const [editingResource, setEditingResource] =
    useState<PreviewResource | null>(null);
  const [addResourceDrawerOpen, setAddResourceDrawerOpen] = useState(false);

  // Auto-open add drawer when ?create=true
  useEffect(() => {
    if (create) {
      setAddResourceDrawerOpen(true);
    }
  }, [create]);

  const { downloadResource } = useResourceDownload();

  const { data: resourceTagsData } = useResourceTagsByCoach(engagementId);

  const {
    data: resourcesData,
    isLoading,
    error,
  } = useResourcesByCoach(engagementId, currentPage, pageSize, selectedTagIds);

  const { data: resourceDetailData } = useResourceDetail(
    selectedResourceId || "",
  );

  useEffect(() => {
    if (resourceDetailData) {
      const detailResource = resourceDetailData;

      const previewData: PreviewResource = {
        id: detailResource.id,
        title: detailResource.title,
        externalLink: detailResource.externalLink,
        uploadModel: detailResource.uploadModel || "file",
        filename: detailResource.filename,
        tags:
          detailResource.assignTagToResources
            ?.filter((tagAssignment) => tagAssignment.tag !== null)
            .map((tagAssignment) => ({
              id: tagAssignment.tag!.id,
              name: tagAssignment.tag!.name,
            })) || [],
      };

      setPreviewResource(previewData);
    }
  }, [resourceDetailData]);

  const handlePaginationChange = (pagination: {
    pageIndex: number;
    pageSize: number;
  }) => {
    startTransition(() => {
      setCurrentPage(pagination.pageIndex + 1);
      setPageSize(pagination.pageSize);
    });
  };

  const list = resourcesData?.data ?? [];
  const rows: ResourceRow[] = list
    .filter((r) => r.uploadModel === "file" || r.uploadModel === "url")
    .map((r) => ({
      ...r,
      title: r.title || r.filename || "Untitled",
      tags:
        "assignTagToResources" in r
          ? (
              r.assignTagToResources as {
                tag: { id: string; name: string } | null;
              }[]
            )
              ?.map((a) => a.tag)
              .filter((t): t is { id: string; name: string } => !!t) || []
          : [],
    }));

  const handleDelete = async (resource: ResourceRow) => {
    if (selectedResourceId === resource.id) {
      setSelectedResourceId(null);
      setPreviewDrawerOpen(false);
    }

    deleteResource.mutate(
      { resourceId: resource.id },
      {
        onSuccess: () => {
          toast.success("Resource deleted");
        },
        onError: (error) => {
          toast.error("Failed to delete resource");
          console.error("Delete error:", error);
        },
      },
    );
  };

  const handleRowClick = (resource: ResourceRow) => {
    setSelectedResourceId(resource.id);
    setPreviewDrawerOpen(true);
  };

  const handleDownload = (resource: ResourceRow) => {
    downloadResource(resource);
  };

  const columns = createResourceColumns({
    onDownload: handleDownload,
    onDelete: handleDelete,
    onRowClick: handleRowClick,
  });

  const isPaginationLoading = isPending || isLoading;

  const paginationInfo =
    resourcesData && "pagination" in resourcesData
      ? {
          pageIndex: currentPage - 1,
          pageSize: pageSize,
          total: resourcesData.pagination.total,
          hasNextPage: resourcesData.pagination.hasNextPage,
          hasPreviousPage: currentPage > 1,
        }
      : undefined;

  const hasTagFilter = selectedTagIds.length > 0;
  const isEmpty = !isLoading && !error && rows.length === 0 && !hasTagFilter;
  const showTable = !isLoading && !error && (rows.length > 0 || hasTagFilter);

  const handleEditFromPreview = () => {
    if (previewResource) {
      setPreviewDrawerOpen(false);
      setEditingResource(previewResource);
      setAddResourceDrawerOpen(true);
    }
  };

  const handleDeleteFromPreview = () => {
    if (previewResource) {
      setPreviewDrawerOpen(false);
      setSelectedResourceId(null);
      deleteResource.mutate(
        { resourceId: previewResource.id },
        {
          onSuccess: () => {
            toast.success("Resource deleted");
          },
          onError: (error) => {
            console.error("Failed to delete resource:", error);
            toast.error("Failed to delete resource");
          },
        },
      );
    }
  };

  const handleClosePreview = () => {
    setPreviewDrawerOpen(false);
    setPreviewResource(null);
    setSelectedResourceId(null);
  };

  const handleCloseResourceDrawer = () => {
    setAddResourceDrawerOpen(false);
    setEditingResource(null);
  };

  const handleAddResource = () => {
    setAddResourceDrawerOpen(true);
  };

  const handleAddResourceSubmit = (data: AddEngagementResourceInput) => {
    const baseData = {
      title: data.title,
      name: data.title,
      uploadModel: data.uploadModel,
      tagIds: data.tagIds,
      engagementId,
      sessionId: data.sessionId,
    };

    const addData =
      data.uploadModel === "file"
        ? {
            ...baseData,
            file: data.file,
          }
        : {
            ...baseData,
            externalLinkUrl: data.externalLinkUrl,
          };

    addResource.mutate(addData, {
      onSuccess: () => {
        toast.success("Resource added");
        handleCloseResourceDrawer();
      },
      onError: (error) => {
        console.error("Failed to add resource:", error);
        toast.error("Failed to add resource");
      },
    });
  };

  const handleUpdateResourceSubmit = (data: UpdateEngagementResourceInput) => {
    if (!editingResource) {
      toast.error("No resource selected for editing");
      return;
    }

    const updateData: UpdateEngagementResourceInput = {
      resourceId: editingResource.id,
      title: data.title || "",
      uploadModel: data.uploadModel,
      externalLinkUrl: data.externalLinkUrl,
      tagIds: data.tagIds || [],
      file: data.file,
    };

    updateResource.mutate(updateData, {
      onSuccess: () => {
        toast.success("Resource updated");
        handleCloseResourceDrawer();
      },
      onError: (error) => {
        console.error("Failed to update resource:", error);
        toast.error("Failed to update resource");
      },
    });
  };

  const handleResourceSubmit = (
    data: AddEngagementResourceInput | UpdateEngagementResourceInput,
  ) => {
    if (editingResource) {
      handleUpdateResourceSubmit(data as UpdateEngagementResourceInput);
    } else {
      handleAddResourceSubmit(data as AddEngagementResourceInput);
    }
  };

  return (
    <>
      <PageSection
        title="Resources"
        action={
          <Button
            variant="primary"
            className="gap-x-2 text-sm"
            onClick={handleAddResource}
          >
            <Plus />
            Add resource
          </Button>
        }
      >
        <TagCombobox
          selectedTags={selectedTagIds}
          onTagsChange={(tags: string[]) => {
            setSelectedTagIds(tags);
            setCurrentPage(1);
          }}
          availableTags={resourceTagsData || []}
          mode="select-only"
          variant="compact"
          placeholder="Filter by tags..."
        />

        {isLoading && (
          <Card>
            <ResourceTableSkeleton />
          </Card>
        )}

        {error && (
          <ErrorStateCard
            title="Error loading resources"
            message="Something went wrong while loading your resources. Please try again."
          />
        )}

        {showTable && (
          <Card>
            <DataTable
              columns={columns}
              manualPagination={true}
              pagination={paginationInfo}
              data={rows}
              onPaginationChange={handlePaginationChange}
              isPaginationLoading={isPaginationLoading}
            />
          </Card>
        )}

        {isEmpty && (
          <Card className="p-0">
            <EmptyState
              icon={BookOpen}
              title="No resources shared yet"
              description="Upload or link materials, guides, and tools to support their progress — everything stays organized here."
            >
              <Button
                variant="primary"
                size="sm"
                className="gap-x-2"
                onClick={handleAddResource}
              >
                <Plus className="h-4 w-4" />
                Add resource
              </Button>
            </EmptyState>
          </Card>
        )}
      </PageSection>

      <ResourcePreviewDrawer
        isOpen={previewDrawerOpen}
        onClose={handleClosePreview}
        onEdit={handleEditFromPreview}
        onDelete={handleDeleteFromPreview}
        onDownload={handleDownload}
        resource={previewResource}
      />
      <ResourceDrawer
        ref={resourceDrawerRef}
        open={addResourceDrawerOpen}
        onOpenChange={handleCloseResourceDrawer}
        onSubmit={handleResourceSubmit}
        engagementId={engagementId}
        resource={editingResource}
        mode={editingResource ? "edit" : "add"}
        isSubmitting={addResource.isPending || updateResource.isPending}
      />
    </>
  );
}
